import React from 'react';
import { createPortal } from 'react-dom';

import { useTourGuide } from '../TourGuideProvider';
import { modalSafeChromeProps } from './modalSafe';
import type { CalloutSide } from '../tasks/types';
import type { TaskTargetRect } from '../engine/TaskEngine';

const GAP = 14;
const CALLOUT_WIDTH = 320;
const CALLOUT_FALLBACK_HEIGHT = 220;
const MARGIN = 12;
const SPOTLIGHT_PAD = 8;
const BOTTOM_BAR = 78;

interface Placement {
  top: number;
  left: number;
  side: CalloutSide;
}

export function placeCallout(
  target: TaskTargetRect,
  side: CalloutSide,
  viewport: { width: number; height: number },
  height: number = CALLOUT_FALLBACK_HEIGHT,
  avoid: TaskTargetRect | null = null,
): Placement {
  if (avoid) {
    const roomRight = viewport.width - (avoid.left + avoid.width) - GAP - MARGIN;
    const roomLeft = avoid.left - GAP - MARGIN;
    const gutter = Math.max(roomRight, roomLeft);

    if (gutter >= CALLOUT_WIDTH) {
      const left =
        roomRight >= CALLOUT_WIDTH
          ? avoid.left + avoid.width + GAP
          : avoid.left - GAP - CALLOUT_WIDTH;
      const top = Math.max(
        MARGIN,
        Math.min(
          target.top + target.height / 2 - height / 2,
          Math.max(MARGIN, viewport.height - BOTTOM_BAR - height),
        ),
      );
      return { side: roomRight >= CALLOUT_WIDTH ? 'right' : 'left', left, top };
    }
  }

  const fits = {
    right: target.left + target.width + GAP + CALLOUT_WIDTH <= viewport.width - MARGIN,
    left: target.left - GAP - CALLOUT_WIDTH >= MARGIN,
    bottom: target.top + target.height + GAP + height <= viewport.height - BOTTOM_BAR,
    top: target.top - GAP - height >= MARGIN,
  };

  const opposite: Record<CalloutSide, CalloutSide> = {
    right: 'left',
    left: 'right',
    top: 'bottom',
    bottom: 'top',
  };

  const chosen: CalloutSide = fits[side]
    ? side
    : fits[opposite[side]]
      ? opposite[side]
      : (['right', 'bottom', 'left', 'top'] as CalloutSide[]).find((s) => fits[s]) ?? side;

  let top: number;
  let left: number;

  if (chosen === 'right' || chosen === 'left') {
    left = chosen === 'right' ? target.left + target.width + GAP : target.left - GAP - CALLOUT_WIDTH;
    top = target.top;
  } else {
    left = target.left + target.width / 2 - CALLOUT_WIDTH / 2;
    top = chosen === 'bottom' ? target.top + target.height + GAP : target.top - GAP - height;
  }

  const clamp = (value: number, min: number, max: number) =>
    Math.max(min, Math.min(value, Math.max(min, max)));

  return {
    side: chosen,
    left: clamp(left, MARGIN, viewport.width - CALLOUT_WIDTH - MARGIN),
    top: clamp(top, MARGIN, viewport.height - BOTTOM_BAR - height),
  };
}

export function TaskSpotlight() {
  const tour = useTourGuide();
  const calloutRef = React.useRef<HTMLElement | null>(null);
  const [calloutHeight, setCalloutHeight] = React.useState(CALLOUT_FALLBACK_HEIGHT);
  const [viewport, setViewport] = React.useState(() => ({
    width: typeof window === 'undefined' ? 0 : window.innerWidth,
    height: typeof window === 'undefined' ? 0 : window.innerHeight,
  }));

  React.useEffect(() => {
    const onResize = () => setViewport({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const task = tour?.task;
  const stepId = task?.step?.id ?? null;
  const description = task?.step?.description ?? '';

  React.useLayoutEffect(() => {
    const el = calloutRef.current;
    if (!el) return;
    const measured = el.offsetHeight;
    if (measured > 0 && measured !== calloutHeight) setCalloutHeight(measured);
  }, [stepId, description, calloutHeight]);

  if (!task?.running || !task.step) return null;

  if (task.step.awaiting) return null;

  const { step, target } = task;
  const placement = target
    ? placeCallout(target, step.side, viewport, calloutHeight, task.avoid)
    : null;

  return createPortal(
    <>
      {step.spotlight && target && (
        <div
          className="tour-task-overlay"
          data-testid="tour-task-overlay"
          aria-hidden="true"
          style={{
            top: target.top - SPOTLIGHT_PAD,
            left: target.left - SPOTLIGHT_PAD,
            width: target.width + SPOTLIGHT_PAD * 2,
            height: target.height + SPOTLIGHT_PAD * 2,
          }}
        />
      )}

      {target && (
        <div
          className="tour-task-ring"
          data-testid="tour-task-ring"
          aria-hidden="true"
          style={{
            top: target.top,
            left: target.left,
            width: target.width,
            height: target.height,
          }}
        />
      )}

      <aside
        ref={calloutRef}
        className="tour-task-callout"
        data-testid="tour-task-callout"
        data-side={placement?.side ?? 'center'}
        data-anchored={target ? 'true' : 'false'}
        {...modalSafeChromeProps}
        role="status"
        aria-live="polite"
        style={
          placement
            ? { top: placement.top, left: placement.left }
            : // Nothing to point at (yet). Centred rather than hidden — the copy
              { bottom: BOTTOM_BAR + 12, left: '50%', transform: 'translateX(-50%)' }
        }
      >
        <p className="tour-task-callout__step">
          Step {Math.min(task.index + 1, task.total)} of {task.total}
        </p>
        <h2 className="tour-task-callout__title">{step.title}</h2>
        <p className="tour-task-callout__body">{step.description}</p>

        {step.lost && (
          <p className="tour-task-callout__warn">
            {step.auto
              ? 'This control is not on screen. Use Skip step to carry on.'
              : 'This control is not on screen. Press Next to carry on.'}
          </p>
        )}

        <div className="tour-task-callout__actions">
          <button
            type="button"
            className="tour-task-callout__btn"
            onClick={() => tour?.taskPrevious()}
            data-testid="tour-task-back"
          >
            Back
          </button>

          <button
            type="button"
            className="tour-task-callout__btn"
            onClick={() => tour?.taskSkipModule()}
            data-testid="tour-task-skip-module"
          >
            Skip module
          </button>

          {step.auto ? (
            <>
              <button
                type="button"
                className="tour-task-callout__btn"
                onClick={() => tour?.taskSkip()}
                data-testid="tour-task-skip"
              >
                Skip step
              </button>
              <span className="tour-task-callout__waiting" data-testid="tour-task-waiting">
                {step.waitingHint ?? 'Working…'}
              </span>
            </>
          ) : (
            <>
              {step.canSkipSegment && (
                <button
                  type="button"
                  className="tour-task-callout__btn"
                  onClick={() => tour?.taskSkipSegment()}
                  data-testid="tour-task-skip-segment"
                >
                  Skip section
                </button>
              )}
              <button
                type="button"
                className="tour-task-callout__btn tour-task-callout__btn--primary"
                onClick={() => tour?.taskNext()}
                data-testid="tour-task-next"
              >
                {step.nextLabel ?? 'Next'}
              </button>
            </>
          )}
        </div>

        {!step.auto && !step.satisfied && step.waitingHint && (
          <p className="tour-task-callout__hint">{step.waitingHint}</p>
        )}
      </aside>
    </>,
    document.body,
  );
}
